// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";

// interfaces
import { IInAppUpiPaymentGroup, InAppUpiPaymentMethod, PaymentMethodProps } from "@payment-ui/interfaces";

// dls
import AddFilledIcon from "@swiggy-private/dls-web-icons/dist/web/generic/addFilled/12";

// widgets
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";
import Alert from "@payment-ui/widgets/alert/Alert";

// test-ids
import { NEW_INAPP_UPI_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

// helpers
import { canEnablePaymentMethod, getPaymentMethodPromoMsg } from "@payment-ui/helpers/Payment";
import { useEffectOnce } from "@swiggy-private/core-ui";

// styles
import { Container, PaymentMethod, IconContainer, Icon, AlertMessage } from "./styles/NewInAppUpi.styled";

interface NewUpiProps extends PaymentMethodProps {
    position?: number;
    methodData: InAppUpiPaymentMethod;
    paymentActions: IInAppUpiPaymentGroup["paymentActions"];
    onClickAddNew?: (value: string) => void;
    onImpressionAddNew?: VoidFunction;
}

function NewInAppUpiPaymentMethod(props: NewUpiProps): ReactElement {
    const promoMsg = getPaymentMethodPromoMsg(props.methodData);
    const isMethodEnabled = useMemo(() => canEnablePaymentMethod(props.methodData, props.isPaymentGroupEnabled), [
        props.isPaymentGroupEnabled,
        props.methodData,
    ]);
    const { onSelect } = props.paymentActions;
    const { onClickAddNew, onImpressionAddNew } = props;

    useEffectOnce(() => {
        if (typeof onImpressionAddNew === "function") onImpressionAddNew();
    });

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            if (typeof onClickAddNew === "function") onClickAddNew(String(props.position));

            e.preventDefault();
            isMethodEnabled && onSelect(props.methodData);
        },
        [onClickAddNew, props.position, props.methodData, isMethodEnabled, onSelect],
    );

    return (
        <PaymentMethod $isDisabled={!isMethodEnabled}>
            <Container onClick={onClick} data-testid={NEW_INAPP_UPI_PAYMENT_METHOD_TEST_IDS.CONTAINER}>
                <IconContainer>
                    <Icon
                        $isDisabled={!isMethodEnabled}
                        data-testid={NEW_INAPP_UPI_PAYMENT_METHOD_TEST_IDS.ICON}
                        Icon={AddFilledIcon}
                        fillColor="primary"
                    />
                </IconContainer>
                <PaymentMethodName
                    displayName={props.methodData.displayName}
                    promoMsg={promoMsg}
                    enabled={isMethodEnabled}
                    data-testid={NEW_INAPP_UPI_PAYMENT_METHOD_TEST_IDS.NAME}
                    color="primary"
                >
                    {!isMethodEnabled && (
                        <AlertMessage>
                            <Alert
                                message={props.methodData.meta.disableMessage}
                                data-testid={NEW_INAPP_UPI_PAYMENT_METHOD_TEST_IDS.DISABLE_MESSAGE}
                            />
                        </AlertMessage>
                    )}
                </PaymentMethodName>
            </Container>
        </PaymentMethod>
    );
}

export default memo(NewInAppUpiPaymentMethod);
